import { gql, useMutation, useLazyQuery } from '@apollo/client'
import { useState, useCallback } from 'react'
import { getApiAuthorizationHeader } from 'lib/auth-token'

const CREATE_FILE = gql`
  mutation CreateFile($input: FileInputSchema!) {
    createFile(input: $input) {
      id
    }
  }
`;

const GET_FILE_UPLOAD_LOCATION = gql`
  query getFileUploadLocation($FileInput: [FileInputSchema!]!) {
    file(input: $FileInput) {
      id
      uploadLocation
    }
  }
`;

const GET_FILE_DOWNLOAD_LOCATION = gql`
  query getFileDownloadLocation($FileInput: [FileInputSchema!]!) {
    file(input: $FileInput) {
      id
      originalName
      downloadLocation
    }
  }
`;

export interface FileUploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

interface UploadFileOptions {
  onProgress?: (progress: FileUploadProgress) => void;
}

const uploadFormData = (
  uploadLocation: string,
  formData: FormData,
  token: string,
  onProgress?: (progress: FileUploadProgress) => void,
  expectedTotal = 0,
) => new Promise<void>((resolve, reject) => {
  const request = new XMLHttpRequest();
  request.open('POST', uploadLocation);
  request.setRequestHeader('Authorization', token);
  request.setRequestHeader('X-Authorization-Method', 'api');

  request.upload.onprogress = (event) => {
    if (!event.lengthComputable) return;
    onProgress?.({
      loaded: event.loaded,
      total: event.total,
      percent: Math.round((event.loaded / event.total) * 100),
    });
  };

  request.onload = () => {
    if (request.status >= 200 && request.status < 300) {
      const total = expectedTotal || 1;
      onProgress?.({
        loaded: total,
        total,
        percent: 100,
      });
      resolve();
      return;
    }

    reject(new Error(`File upload failed with status ${request.status}`));
  };

  request.onerror = () => reject(new Error('File upload failed because the network request failed'));
  request.onabort = () => reject(new Error('File upload was cancelled'));
  request.ontimeout = () => reject(new Error('File upload timed out'));
  request.send(formData);
});

const useFileUpload = () => {
  const [error, setError] = useState<string>('');
  
  const [createFile] = useMutation(CREATE_FILE, {
    onError: (err) => {
      setError(err.message);
    }
  });
  
  const [getFileUploadLocation] = useLazyQuery(GET_FILE_UPLOAD_LOCATION, {
    onError: (err) => {
      setError(err.message);
    }
  });
  
  const [getFileDownloadLocation] = useLazyQuery(GET_FILE_DOWNLOAD_LOCATION, {
    onError: (err) => {
      setError(err.message);
    }
  });

  const uploadFile = useCallback(async (file: File, options: UploadFileOptions = {}) => {
    setError('');
    try {
      // 1. Create file record
      const fileResp = await createFile({ variables: { input: { originalName: file.name } } });
      const newFileId = fileResp.data.createFile.id;

      // 2. Get upload location
      const uploadLocResp = await getFileUploadLocation({ variables: { FileInput: [{ id: newFileId }] } });
      const uploadLocation = uploadLocResp.data?.file?.[0]?.uploadLocation;
      if (!uploadLocation) {
        throw new Error('No uploadLocation returned from server');
      }

      // 3. Upload file via REST
      const formData = new FormData();
      formData.append('file', file);

      const token = getApiAuthorizationHeader();
      await uploadFormData(uploadLocation, formData, token, options.onProgress, file.size);

      // 4. (Optional) Get download location
      const downloadLocResp = await getFileDownloadLocation({ variables: { FileInput: [{ id: newFileId }] } });
      const downloadLocation = downloadLocResp.data?.file?.[0]?.downloadLocation;

      return {
        fileId: newFileId,
        downloadLocation,
      };
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        // Handle other kinds of thrown values, e.g. strings or objects
        setError(String(err));
      }
      console.error(err);
      return null;
    }
  }, [createFile, getFileUploadLocation, getFileDownloadLocation]);

  return {
    error,
    uploadFile,
  };
};

export default useFileUpload;
